// Cross-org adoption of data collection categories (PICs), used by the Data Flows
// collection page to recommend the categories most businesses select. Snapshot of the
// sanitized dataset (556 organizations with consumer collection; consumer collection groups
// only), regenerated 2026-08-20. Keyed by the stable `personal_information_category.pic_key`;
// entries that no longer match a catalog key silently drop out, same convention as
// vendorPopularity.
//
// Counted per ORGANIZATION, not per collection group: an org can hold several consumer
// collection groups, so a group-keyed count answers "what share of collection groups" while
// `categoryPopularityLabel` below claims "N in 10 businesses". The earlier snapshot counted
// groups and so understated every rate.
//
// Refresh by re-running this SQL against a sanitized DB load and updating the table:
//
//   with cg as (select cgp.id, cgp.organization_id
//               from collection_group cgp
//               join organization o on o.id = cgp.organization_id and o.deleted_at is null
//               where cgp.deleted_at is null
//                 and cgp.collection_group_type = 'BUSINESS_TO_CONSUMER'),
//   tot as (select count(distinct cg.organization_id) as n
//           from collection_group_collected_pic ccp
//           join cg on cg.id = ccp.collection_group_id
//           where ccp.deleted_at is null)
//   select pic.pic_key,
//          round(count(distinct cg.organization_id)::numeric / (select n from tot), 2)
//   from collection_group_collected_pic ccp
//   join cg on cg.id = ccp.collection_group_id
//   join personal_information_category pic on pic.id = ccp.pic_id
//   where ccp.deleted_at is null
//     and pic.organization_id is null and pic.pic_key is not null and not pic.deprecated
//   group by pic.pic_key
//   having count(distinct cg.organization_id)::numeric / (select n from tot) >= 0.30
//   order by 2 desc;
//
// NOTE: sensitive categories are additionally excluded at the call site regardless of
// adoption — a privacy product must never nudge orgs toward claiming sensitive
// collection from popularity alone.

export type CategoryPopularity = { picKey: string; adoption: number };

export const CATEGORY_POPULARITY: CategoryPopularity[] = [
  { picKey: "name", adoption: 0.99 },
  { picKey: "email-address", adoption: 0.99 },
  { picKey: "online-identifiers", adoption: 0.98 },
  { picKey: "internet-activity", adoption: 0.97 },
  { picKey: "phone-number", adoption: 0.95 },
  { picKey: "postal-address", adoption: 0.94 },
  { picKey: "purchases", adoption: 0.92 },
  { picKey: "credit-debit-card-number", adoption: 0.92 },
  { picKey: "geolocation-information", adoption: 0.76 },
  { picKey: "inferences", adoption: 0.63 },
  { picKey: "direct-communications-with-you-consumers", adoption: 0.48 },
];

// Above the observed adoption cliff (92% → 76%): the same top 8 categories qualify as under the
// old group-keyed snapshot — only the rates moved, not the recommended set.
export const CATEGORY_RECOMMENDATION_THRESHOLD = 0.8;

const byKey = new Map(CATEGORY_POPULARITY.map((c) => [c.picKey, c]));

export function categoryAdoption(picKey: string | undefined): number | undefined {
  return picKey != null ? byKey.get(picKey)?.adoption : undefined;
}

export function isPopularCategoryKey(picKey: string | undefined): boolean {
  const adoption = categoryAdoption(picKey);
  return adoption != null && adoption >= CATEGORY_RECOMMENDATION_THRESHOLD;
}

// Rounded to "N in 10" — false precision would overstate what a snapshot can claim.
export function categoryPopularityLabel(adoption: number): string {
  return `Collected by ${Math.round(adoption * 10)} in 10 businesses that use TrueVault`;
}
